"use client";

import { c as _c } from "react/compiler-runtime";
import { EyeIcon, EyeOffIcon, PencilIcon, PlusIcon, TrashIcon, WrenchIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { McpServer } from "@/hooks/useMcpServers";
interface McpServersDialogProps {
  servers: McpServer[];
  onAddServer: (server: Omit<McpServer, "id">) => McpServer;
  onUpdateServer: (id: string, updates: Partial<Omit<McpServer, "id">>) => void;
  onRemoveServer: (id: string) => void;
  onToggleServer: (id: string) => void;
}
export function McpServersDialog(t0) {
  const $ = _c(61);
  const {
    servers,
    onAddServer,
    onUpdateServer,
    onRemoveServer,
    onToggleServer
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [showApiKey, setShowApiKey] = useState(t1);
  const [formName, setFormName] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [formApiKey, setFormApiKey] = useState("");
  let t2;
  if ($[1] !== servers) {
    t2 = servers.filter(_temp);
    $[1] = servers;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const enabledCount = t2.length;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setFormName("");
      setFormUrl("");
      setFormApiKey("");
      setIsAddingNew(false);
      setEditingId(null);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const resetForm = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      resetForm();
      setIsAddingNew(true);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleStartAdd = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = server => {
      setFormName(server.name);
      setFormUrl(server.url);
      setFormApiKey(server.apiKey);
      setEditingId(server.id);
      setIsAddingNew(false);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleStartEdit = t5;
  let t6;
  if ($[6] !== editingId || $[7] !== formApiKey || $[8] !== formName || $[9] !== formUrl || $[10] !== onAddServer || $[11] !== onUpdateServer) {
    t6 = () => {
      if (!formName.trim() || !formUrl.trim()) {
        return;
      }
      if (editingId) {
        onUpdateServer(editingId, {
          name: formName.trim(),
          url: formUrl.trim(),
          apiKey: formApiKey.trim()
        });
      } else {
        onAddServer({
          name: formName.trim(),
          url: formUrl.trim(),
          apiKey: formApiKey.trim(),
          enabled: true
        });
      }
      resetForm();
    };
    $[6] = editingId;
    $[7] = formApiKey;
    $[8] = formName;
    $[9] = formUrl;
    $[10] = onAddServer;
    $[11] = onUpdateServer;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSave = t6;
  let t7;
  if ($[13] !== editingId || $[14] !== onRemoveServer) {
    t7 = id => {
      onRemoveServer(id);
      if (editingId === id) {
        resetForm();
      }
    };
    $[13] = editingId;
    $[14] = onRemoveServer;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleDelete = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = id_0 => {
      setShowApiKey(prev => ({
        ...prev,
        [id_0]: !prev[id_0]
      }));
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const toggleApiKeyVisibility = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <WrenchIcon className="h-4 w-4" />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== enabledCount) {
    t10 = enabledCount > 0 && <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-medium">{enabledCount}</span>;
    $[18] = enabledCount;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = <TooltipTrigger asChild={true}><DialogTrigger asChild={true}><Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label="MCP Servers">{t9}{t10}</Button></DialogTrigger></TooltipTrigger>;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== enabledCount) {
    t12 = <TooltipContent><p>MCP Servers ({enabledCount} active)</p></TooltipContent>;
    $[22] = enabledCount;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <Tooltip>{t11}{t12}</Tooltip>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DialogHeader><DialogTitle>MCP Servers</DialogTitle><DialogDescription>Connect to Model Context Protocol servers to extend AI capabilities with external tools.</DialogDescription></DialogHeader>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== editingId || $[29] !== handleDelete || $[30] !== isAddingNew || $[31] !== onToggleServer || $[32] !== servers) {
    t15 = servers.length > 0 && !isAddingNew && !editingId && <div className="space-y-2">{servers.map(server_0 => <div key={server_0.id} className="bg-muted/50 flex items-center justify-between rounded-lg border p-3"><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="font-medium">{server_0.name}</span><Switch checked={server_0.enabled} onCheckedChange={() => onToggleServer(server_0.id)} className="scale-75" /></div><p className="text-muted-foreground truncate text-xs">{server_0.url}</p></div><div className="flex items-center gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleStartEdit(server_0)} aria-label="Edit server" title="Edit server"><PencilIcon className="h-3.5 w-3.5" /></Button><Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => handleDelete(server_0.id)} aria-label="Delete server" title="Delete server"><TrashIcon className="h-3.5 w-3.5" /></Button></div></div>)}</div>;
    $[28] = editingId;
    $[29] = handleDelete;
    $[30] = isAddingNew;
    $[31] = onToggleServer;
    $[32] = servers;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== editingId || $[35] !== formApiKey || $[36] !== formName || $[37] !== formUrl || $[38] !== handleSave || $[39] !== isAddingNew || $[40] !== showApiKey) {
    t16 = (isAddingNew || editingId) && <div className="space-y-4 rounded-lg border p-4"><div className="space-y-2"><Label htmlFor="mcp-name">Name</Label><Input id="mcp-name" placeholder="My MCP Server" value={formName} onChange={e => setFormName(e.target.value)} /></div><div className="space-y-2"><Label htmlFor="mcp-url">URL</Label><Input id="mcp-url" placeholder="https://api.example.com/mcp" value={formUrl} onChange={e_0 => setFormUrl(e_0.target.value)} /></div><div className="space-y-2"><Label htmlFor="mcp-api-key">API Key (optional)</Label><div className="relative"><Input id="mcp-api-key" type={showApiKey.form ? "text" : "password"} placeholder="sk-..." value={formApiKey} onChange={e_1 => setFormApiKey(e_1.target.value)} className="pr-10" /><Button type="button" variant="ghost" size="icon" className="absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2" onClick={() => toggleApiKeyVisibility("form")} aria-label={showApiKey.form ? "Hide API key" : "Show API key"} title={showApiKey.form ? "Hide API key" : "Show API key"}>{showApiKey.form ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}</Button></div></div><div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={resetForm}>Cancel</Button><Button size="sm" onClick={handleSave} disabled={!formName.trim() || !formUrl.trim()}>{editingId ? "Save" : "Add Server"}</Button></div></div>;
    $[34] = editingId;
    $[35] = formApiKey;
    $[36] = formName;
    $[37] = formUrl;
    $[38] = handleSave;
    $[39] = isAddingNew;
    $[40] = showApiKey;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== isAddingNew || $[43] !== servers.length) {
    t17 = servers.length === 0 && !isAddingNew && <div className="text-muted-foreground py-8 text-center text-sm"><WrenchIcon className="mx-auto mb-2 h-8 w-8 opacity-50" /><p>No MCP servers configured</p><p className="mt-1 text-xs">Add a server to extend AI capabilities</p></div>;
    $[42] = isAddingNew;
    $[43] = servers.length;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
    t18 = <div className="max-h-[400px] space-y-4 overflow-y-auto py-4">{t15}{t16}{t17}</div>;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== editingId || $[50] !== isAddingNew) {
    t19 = !isAddingNew && !editingId && <Button onClick={handleStartAdd} className="gap-2"><PlusIcon className="h-4 w-4" />Add MCP Server</Button>;
    $[49] = editingId;
    $[50] = isAddingNew;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t19) {
    t20 = <DialogFooter>{t19}</DialogFooter>;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t18 || $[55] !== t20) {
    t21 = <DialogContent className="sm:max-w-[500px]">{t14}{t18}{t20}</DialogContent>;
    $[54] = t18;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== isOpen || $[58] !== t13 || $[59] !== t21) {
    t22 = <Dialog open={isOpen} onOpenChange={setIsOpen}>{t13}{t21}</Dialog>;
    $[57] = isOpen;
    $[58] = t13;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  return t22;
}
function _temp(s) {
  return s.enabled;
}